'use client';

import {
	ENUM_UTILS,
	OpenPosition,
	UIMarket,
	UIOrderType,
	calculatePotentialProfit,
} from '@velocity-exchange/common';
import useOraclePrice from '../useOraclePrice';
import { useMemo } from 'react';
import {
	BASE_PRECISION_EXP,
	BN,
	BigNum,
	PRICE_PRECISION,
	PRICE_PRECISION_EXP,
	PositionDirection,
	QUOTE_PRECISION_EXP,
	User,
	ZERO,
} from '@velocity-exchange/sdk';
import { prettyPrintPriceBasedOnTick } from 'src/utils/formatting';
import NumLib from 'src/utils/NumLib';
import {
	getMessageInfoForOrderViolation,
	getPriceInputLabel,
	getShouldShowSecondaryPriceBox,
} from 'src/utils/trade';
import { useMarketStepSize } from '../useMarketStepSize';
import { ZERO_QUOTE_BIGNUM } from 'src/constants/math';
import { usePerpMarketsLookup } from '../perpMarkets/usePerpMarketsLookup';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';

import usePreviewOrder from 'src/components/TradeForm/hooks/usePreviewOrder';
import useL2StateForMarket from '../useL2StateForMarket';

type ClosePositionStateEngineProps = {
	market: number;
	selectedOrderType: UIOrderType;
	priceBoxValue: number;
	secondaryPriceBoxValue: number;
	currentPositionDirection: 'long' | 'short';
	/**
	 * Seems that, due to a race condition, this can be `undefined` once the
	 * position has been successfully closed but the modal hasn't yet unmounted.
	 */
	openPositionInfo: OpenPosition | undefined;
	takerFeeBps: number;
	targetMarketTickSizeExponent: number;
	baseAmountOfClose: BigNum;
	user: User;
};

export function getQuotePriceToUse(
	selectedOrderType: UIOrderType,
	orderDirection: 'long' | 'short' | 'buy' | 'sell',
	marketPrice: BigNum,
	currentOraclePrice: BigNum,
	limitOrTriggerPrice: BigNum,
	triggerMarketImmediateExecutionPrice: BigNum,
	triggerLimitPrice: BigNum
) {
	let priceToUse: BigNum;

	const isBuyOrder = orderDirection === 'long' || orderDirection === 'buy';
	const isSellOrder = orderDirection === 'short' || orderDirection === 'sell';

	switch (selectedOrderType) {
		case 'market':
			priceToUse = marketPrice;
			break;
		case 'limit':
			priceToUse = limitOrTriggerPrice;

			// long position - short close - current market price above limit price will lead to immediate execution of market order
			if (isSellOrder && marketPrice.gte(limitOrTriggerPrice)) {
				priceToUse = marketPrice;
			}

			// short position - long close - current market price below limit price will lead to immediate execution of market order
			if (isBuyOrder && marketPrice.lte(limitOrTriggerPrice)) {
				priceToUse = marketPrice;
			}

			break;
		case 'stopMarket':
			priceToUse = limitOrTriggerPrice;

			// short position stop loss - current oracle price above trigger price will lead to immediate execution of market order
			if (isBuyOrder && currentOraclePrice.gte(limitOrTriggerPrice)) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			// long position stop loss - current oracle price below trigger price will lead to immediate execution of market order
			if (isSellOrder && currentOraclePrice.lte(limitOrTriggerPrice)) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			break;
		case 'takeProfitMarket':
			priceToUse = limitOrTriggerPrice;

			// short position - take profit means price below entry - current oracle price below trigger price will lead to immediate execution of market order
			if (isBuyOrder && currentOraclePrice.lte(limitOrTriggerPrice)) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			// long position - take profit means price above entry - current oracle price above trigger price will lead to immediate execution of market order
			if (isSellOrder && currentOraclePrice.gte(limitOrTriggerPrice)) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}
			break;
		case 'stopLimit':
			priceToUse = triggerLimitPrice;

			// short position stop loss limit
			// - current oracle price above trigger price will lead to immediate placement of limit order (doesn't affect pnl)
			// - long close order - current market price below limit order will lead to immediate execution of market order
			if (
				isBuyOrder &&
				currentOraclePrice.gte(limitOrTriggerPrice) &&
				marketPrice.lte(limitOrTriggerPrice)
			) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			// long position stop loss limit
			// - current oracle price below trigger price will lead to immediate execution of market order (doesn't affect pnl)
			// - short close limit order - current market above below limit order will lead to immediate execution of market order
			if (
				isSellOrder &&
				currentOraclePrice.lte(limitOrTriggerPrice) &&
				marketPrice.gte(limitOrTriggerPrice)
			) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}
			break;
		case 'takeProfitLimit':
			priceToUse = triggerLimitPrice;

			// short position take profit limit
			// - current oracle price below trigger price will lead to immediate execution of market order (doesn't affect pnl)
			// - long close limit order - current market price below limit order will lead to immediate execution of market order
			if (
				isBuyOrder &&
				currentOraclePrice.lte(limitOrTriggerPrice) &&
				marketPrice.lte(triggerLimitPrice)
			) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}

			// long position take profit limit
			// - current oracle price above trigger price will lead to immediate execution of market order  (doesn't affect pnl)
			// - short close limit order - current market price above limit order will lead to immediate execution of market order
			if (
				isSellOrder &&
				currentOraclePrice.gte(limitOrTriggerPrice) &&
				marketPrice.gte(triggerLimitPrice)
			) {
				priceToUse = triggerMarketImmediateExecutionPrice;
			}
			break;
		case 'oracle':
			priceToUse = marketPrice;
			break;
		case 'oracleLimit':
			priceToUse = limitOrTriggerPrice.add(currentOraclePrice);
			break;
		default:
			console.warn(
				'Unhandled order type in Close Position Popup',
				selectedOrderType
			);
			return BigNum.zero(PRICE_PRECISION_EXP);
	}

	return priceToUse;
}

const useClosePositionStateEngine = ({
	market,
	selectedOrderType,
	priceBoxValue,
	secondaryPriceBoxValue,
	currentPositionDirection,
	takerFeeBps,
	openPositionInfo,
	targetMarketTickSizeExponent,
	baseAmountOfClose,
	user,
}: ClosePositionStateEngineProps) => {
	const uiMarket = UIMarket.createPerpMarket(market);
	const marketId = uiMarket.marketId;
	const perpMarketsLookup = usePerpMarketsLookup();
	const marketSymbol = perpMarketsLookup[marketId.marketIndex].symbol;
	const closePositionOrderDirection =
		currentPositionDirection === 'long' ? 'short' : 'long';

	const stepSizeNum = useMarketStepSize(marketId);
	const closeSizeNum = baseAmountOfClose.toNum();

	// App Utility Hooks
	const currentOraclePrice = useOraclePrice(marketId);

	// Use usePreviewOrder hook to get price impact info
	const previewOrderParams = usePreviewOrder(marketId);
	const priceImpactInfo = previewOrderParams.priceImpact;
	const currentMarkPrice = BigNum.from(
		priceImpactInfo?.markPrice.toNumber() ?? 0,
		QUOTE_PRECISION_EXP
	);

	const priceBoxValueBigNum = BigNum.fromPrint(
		priceBoxValue.toString(),
		PRICE_PRECISION_EXP
	);
	const secondaryPriceBoxValueBigNum = BigNum.fromPrint(
		secondaryPriceBoxValue.toString(),
		PRICE_PRECISION_EXP
	);
	const quoteSizeOfClose = getQuoteAmountOfClose().abs();

	const priceInputLabel = getPriceInputLabel(selectedOrderType);

	const l2State = useL2StateForMarket(
		marketId.marketIndex,
		marketId.marketType
	);

	// Show Secondary Price Box
	const showSecondaryPriceBox =
		getShouldShowSecondaryPriceBox(selectedOrderType);

	const errorMessageInfo = getMessageInfoForOrderViolation(
		selectedOrderType,
		priceBoxValue,
		currentMarkPrice.toNum() ?? 0,
		false,
		secondaryPriceBoxValue,
		closePositionOrderDirection,
		currentOraclePrice?.toNum() ?? 0,
		marketSymbol,
		closeSizeNum,
		stepSizeNum,
		'closePosition'
	);

	const calculatedValues = useMemo(() => {
		// # Initial Values
		let estExitPriceUIVal = '';
		let liqPriceVal = '';
		let priceImpactUIVal = '';

		let newEstimatedPnl = 0;
		let newEstimatedPnlBeforeFees = 0;
		let newEstimatedTakerFee = 0;
		let newNotionalSizeAtEntry = 0;
		let newNotionalSizeAtExit = 0;

		// # Base Calculations
		let estExitPriceOnImmediateExecution: BN;
		let priceImpactOnImmediateExecution: BN;

		if (!openPositionInfo) {
			return {
				liquidationPrice: liqPriceVal,
				exitPrice: estExitPriceUIVal,
				exitPriceNum: 0,
				estPriceImpact: priceImpactUIVal,
				pnlBeforeFees: newEstimatedPnlBeforeFees,
				notionalSizeAtExit: newNotionalSizeAtExit,
				notionalSizeAtEntry: newNotionalSizeAtEntry,
				takerFee: newEstimatedTakerFee,
				realisedPnl: newEstimatedPnl,
			};
		}

		if (priceImpactInfo) {
			// Use price impact data from orderParamsClient
			estExitPriceOnImmediateExecution = priceImpactInfo.entryPrice;
			priceImpactOnImmediateExecution = priceImpactInfo.priceImpact;
		} else {
			// Default fallback if no price impact data available
			estExitPriceOnImmediateExecution = currentMarkPrice.val;
			priceImpactOnImmediateExecution = ZERO;
		}

		const estExitPriceOnImmediateExecutionBigNum = BigNum.from(
			estExitPriceOnImmediateExecution,
			PRICE_PRECISION_EXP
		);

		// # Calculate Price Impact
		const priceImpactBigNum = BigNum.from(
			priceImpactOnImmediateExecution ? priceImpactOnImmediateExecution : 0,
			PRICE_PRECISION_EXP
		).shift(new BN(2), true);
		priceImpactUIVal = priceImpactBigNum.gtZero()
			? priceImpactBigNum.toFixed(4)
			: '0';

		// # Calculate Est Exit Price
		const isZeroClosePosition = baseAmountOfClose.eqZero();
		const closeDirectionOrderbookTopPrice =
			(openPositionInfo.direction === 'long'
				? l2State.asks[0]?.price
				: l2State.bids[0]?.price) ?? ZERO;

		const estExitPriceBigNum = getQuotePriceToUse(
			selectedOrderType,
			closePositionOrderDirection,
			isZeroClosePosition
				? BigNum.from(closeDirectionOrderbookTopPrice, PRICE_PRECISION_EXP)
				: estExitPriceOnImmediateExecutionBigNum,
			currentOraclePrice ?? ZERO_QUOTE_BIGNUM,
			priceBoxValueBigNum,
			estExitPriceOnImmediateExecutionBigNum,
			secondaryPriceBoxValueBigNum
		);

		estExitPriceUIVal = prettyPrintPriceBasedOnTick(
			estExitPriceBigNum,
			targetMarketTickSizeExponent
		);

		// # Calculate Realised Pnl
		const {
			estimatedProfit,
			estimatedProfitBeforeFees,
			estimatedTakerFee,
			notionalSizeAtEntry,
			notionalSizeAtExit,
		} = calculatePotentialProfit({
			currentPositionSize: BigNum.from(
				openPositionInfo.baseSize,
				BASE_PRECISION_EXP
			).abs(),
			currentPositionDirection: ENUM_UTILS.toObj(openPositionInfo.direction),
			tradeDirection:
				openPositionInfo.direction === 'long'
					? PositionDirection.SHORT
					: PositionDirection.LONG,
			exitBaseSize: baseAmountOfClose,
			exitPrice:
				selectedOrderType === 'market' &&
				previewOrderParams.priceImpact?.entryPrice
					? BigNum.from(
							previewOrderParams.priceImpact.entryPrice,
							PRICE_PRECISION_EXP
						)
					: estExitPriceBigNum,
			currentPositionEntryPrice: BigNum.from(
				openPositionInfo.entryPrice,
				PRICE_PRECISION_EXP
			),
			isMarketOrder: selectedOrderType == 'market',
			takerFeeBps,
		});

		newEstimatedPnl = estimatedProfit?.toNum() ?? 0;
		newEstimatedPnlBeforeFees = estimatedProfitBeforeFees?.toNum() ?? 0;
		newEstimatedTakerFee = estimatedTakerFee.toNum();
		newNotionalSizeAtEntry = notionalSizeAtEntry.toNum();
		newNotionalSizeAtExit = notionalSizeAtExit.toNum();

		// # Calculate Liquidation Price
		let newLiquidationPrice = user.liquidationPriceAfterClose(
			openPositionInfo.marketIndex,
			baseAmountOfClose.val,
			estExitPriceOnImmediateExecution
		);

		if (baseAmountOfClose.val.gte(openPositionInfo.baseSize)) {
			newLiquidationPrice = new BN(-1);
		}

		if (newLiquidationPrice.lten(0)) {
			liqPriceVal = PRICE_FALLBACK_DISPLAY_VALUE;
		} else if (newLiquidationPrice.gt(new BN(1_000_000).mul(PRICE_PRECISION))) {
			liqPriceVal = '>$1M';
		} else {
			const liqPriceNum = NumLib.formatBn.toRawNum(
				newLiquidationPrice,
				PRICE_PRECISION
			);

			const liqPriceUI = NumLib.formatNum.toDisplayPrice(liqPriceNum);
			liqPriceVal = `$${liqPriceUI}`;
		}

		return {
			liquidationPrice: liqPriceVal,
			exitPriceNum: estExitPriceBigNum.toNum(),
			exitPrice: estExitPriceUIVal,
			estPriceImpact: priceImpactUIVal,
			pnlBeforeFees: newEstimatedPnlBeforeFees,
			notionalSizeAtExit: newNotionalSizeAtExit,
			notionalSizeAtEntry: newNotionalSizeAtEntry,
			takerFee: newEstimatedTakerFee,
			realisedPnl: newEstimatedPnl,
		};
	}, [
		priceBoxValue,
		secondaryPriceBoxValue,
		closeSizeNum,
		openPositionInfo?.entryPrice.toNumber(),
		priceImpactInfo,
	]);

	function getQuoteAmountOfClose() {
		const priceToUse = getQuotePriceToUse(
			selectedOrderType,
			closePositionOrderDirection,
			currentMarkPrice,
			currentOraclePrice ?? ZERO_QUOTE_BIGNUM,
			priceBoxValueBigNum,
			currentMarkPrice,
			secondaryPriceBoxValueBigNum
		);

		const closeAmountQuoteValue = priceToUse
			.mul(baseAmountOfClose)
			.shiftTo(PRICE_PRECISION_EXP);

		return closeAmountQuoteValue.val;
	}

	return {
		priceBoxHeading: priceInputLabel,
		showSecondaryPriceBox,
		errorMessageInfo,
		liquidationPrice: calculatedValues.liquidationPrice,
		exitPriceNum: calculatedValues.exitPriceNum,
		exitPrice: calculatedValues.exitPrice,
		estPriceImpact: calculatedValues.estPriceImpact,
		pnlBeforeFees: calculatedValues.pnlBeforeFees,
		notionalSizeAtExit: calculatedValues.notionalSizeAtExit,
		notionalSizeAtEntry: calculatedValues.notionalSizeAtEntry,
		realisedPnl: calculatedValues.realisedPnl,
		takerFee: calculatedValues.takerFee,
		quoteSizeOfClose,
		priceImpactInfo,
	};
};

export default useClosePositionStateEngine;
